import createClient, { type Middleware } from 'openapi-fetch'
import { runtimeConfig } from '@/shared/config'

// Общий HTTP-клиент. Cookie-auth: credentials: 'include' гонит httpOnly-cookie
// сессии в каждом запросе на тот же origin (/api/*).
// Типы paths появляются после `pnpm gen:api` (Hey API openapi-ts). До этого —
// осознанно untyped client: `any` (не `never`) — иначе параметры запросов
// схлопываются в unassignable-типы у всех callers.
// biome-ignore lint/suspicious/noExplicitAny: временно до codegen
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Paths = any

const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(request.method)) {
      const token = readCookie('csrf_token')
      if (token) request.headers.set('X-CSRF-Token', token)
    }
    return request
  },
}

// Hub-SSO держит access-cookie короткой, а живучесть сессии — в refresh-cookie
// (host-only на issuer'е, Path=/auth). Обе httpOnly: из JS не видно ни срока
// access, ни наличия refresh — отличить «протухла минуту назад» от «сессии нет»
// можно только сходив в refresh.
//
// Контуров два и они независимы (`plan-customer/00-architecture § 3`): сотрудник
// продлевается на employee-backend (id.stit.tech), заказчик — на company-backend
// (api.hub.stit.tech, cookie stit_customer). Оба идут мимо apiClient: 401 самого
// refresh ушёл бы на второй круг.
export type SessionKind = 'employee' | 'customer'

// Последний известный контур: его называет /auth/me. Переживает перезагрузку —
// иначе каждый холодный старт стучится в оба issuer'а. Это подсказка «куда идти
// первым», а не аутентификация: cookie всё равно решает.
const KIND_KEY = 'auth:kind'

let lastKind: SessionKind | null = readStoredKind()

function readStoredKind(): SessionKind | null {
  try {
    const raw = localStorage.getItem(KIND_KEY)
    return raw === 'employee' || raw === 'customer' ? raw : null
  } catch {
    return null
  }
}

export function rememberSessionKind(kind: SessionKind | null): void {
  lastKind = kind
  try {
    if (kind) localStorage.setItem(KIND_KEY, kind)
    else localStorage.removeItem(KIND_KEY)
  } catch {
    // приватный режим/выключенный storage — контур останется в памяти вкладки
  }
}

export function lastSessionKind(): SessionKind | null {
  return lastKind
}

function refreshUrl(kind: SessionKind): string {
  return kind === 'customer'
    ? `${runtimeConfig.HUB_API_BASE_URL}/auth/customer/refresh`
    : `${runtimeConfig.ID_BASE_URL}/auth/refresh`
}

let refreshInFlight: Promise<boolean> | null = null

export function refreshSession(): Promise<boolean> {
  // Refresh ротирующий: параллельные попытки погасили бы друг друга и выбросили
  // живого пользователя, поэтому все 401, пришедшие одновременно, ждут один и
  // тот же промис — независимо от того, какой контур он в итоге продлит.
  refreshInFlight ??= runRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

async function runRefresh(): Promise<boolean> {
  // Контур известен — идём только в него. Холодный старт (в том числе гость,
  // у которого сессии нет вовсе) пробует employee, затем customer.
  const order: SessionKind[] = lastKind ? [lastKind] : ['employee', 'customer']
  for (const kind of order) {
    if (await refreshOne(kind)) {
      rememberSessionKind(kind)
      expired = false
      return true
    }
  }
  // Сессия была и продлить её не вышло — это истечение, а не «не заходил».
  // Отличие видно только здесь, и от него зависит текст на экране входа.
  if (lastKind) expired = true
  return false
}

let expired = false

export function sessionExpired(): boolean {
  return expired
}

function refreshOne(kind: SessionKind): Promise<boolean> {
  return fetch(refreshUrl(kind), { method: 'POST', credentials: 'include' })
    .then((res) => res.ok)
    .catch(() => false)
}

export const apiClient = createClient<Paths>({
  baseUrl: runtimeConfig.API_BASE_URL,
  credentials: 'include',
})

apiClient.use(csrfMiddleware)

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1]
}
